// [S-060] AuthProvider — owns the in-memory session JWT.
//
// Master Rule: JWT lives in React state ONLY. Never written to
// localStorage / sessionStorage / IndexedDB. Page reload triggers a
// re-login (interim — once the FirebaseAuthAdapter is wired the SDK
// handles silent re-auth via httpOnly cookies on Firebase's domain).
//
// Refresh is opportunistic: if a request is about to fire and the JWT
// is within `REFRESH_LEAD_MS` of expiry, the AuthProvider awaits a
// refresh first. The auth header builder helper at `useAuthHeaders`
// embeds this without callers thinking about it.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthAdapter, LoginRequest, WebSession } from "./types";
import {
  ActiveTenantContext,
  deriveEffectiveSession,
} from "./activeTenantContext";
import { SessionExpiredError } from "./sessionExpired";

const REFRESH_LEAD_MS = 60 * 1000; // start refresh 60 s before expiry
// [S-202] Role-change refresh heartbeat. Polls the auth adapter every
// ROLE_REFRESH_INTERVAL_MS to pick up custom-claim changes (role,
// accessTier) within the SLA window. Firebase ID-token forceRefresh
// goes to the IDP each call but is cheap — we trade a per-minute
// round-trip for sub-60s propagation of role demotions / promotions.
const ROLE_REFRESH_INTERVAL_MS = 60 * 1000;

type AuthState =
  // Initial state on mount: we don't yet know if there's a persisted session.
  // Guards must render a loader (NOT redirect to /login) while this holds, or
  // every page refresh bounces an authenticated user to login.
  | { status: "initializing" }
  | { status: "anonymous" }
  | { status: "authenticating" }
  | { status: "authenticated"; session: WebSession }
  // [S-637 slice 2] The session lapsed WHILE the user was working — refresh
  // failed past expiry. Distinct from `anonymous` (never signed in): we retain
  // the last session so the authenticated route tree stays MOUNTED (component
  // state — a half-filled draft, an open review — survives) and the global
  // re-auth modal can offer recovery in place instead of a hard bounce to
  // /login. Clears back to `authenticated` on successful re-auth.
  | { status: "expired"; session: WebSession }
  | { status: "error"; message: string };

type AuthContextValue = {
  state: AuthState;
  signIn: (request: LoginRequest) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * [S-637 slice 2] Re-authenticate an EXPIRED session in place. Unlike
   * `signIn`, it does NOT transition through the global `authenticating` state
   * (which would drop the app back to the Login routes and unmount the work in
   * progress). It resolves to `authenticated` on success, and throws on failure
   * so the modal can show an inline error while the tree — and `expired` — stay
   * put. The caller owns its own submitting/error UI.
   */
  reauthenticate: (request: LoginRequest) => Promise<void>;
  /** Returns the current JWT, refreshing first if it's within the lead window. */
  getFreshJwt: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  auth,
  children,
  onSignOut,
  roleRefreshIntervalMs,
}: {
  auth: AuthAdapter;
  children: ReactNode;
  /**
   * [S-203] Hook fired AFTER the adapter signs out and local auth state
   * resets to "anonymous". The app passes a TanStack Query
   * `queryClient.clear()` here so cached data from the previous user
   * never leaks into the next session. Optional — unit tests that don't
   * need cache flushing can omit it.
   */
  onSignOut?: () => void;
  /**
   * [S-202] Override the role-refresh heartbeat interval. Defaults to
   * 60 s in production; tests pass a smaller value (or 0 to disable).
   */
  roleRefreshIntervalMs?: number;
}): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: "initializing" });
  // Hold the refresh promise so concurrent callers reuse a single in-flight
  // refresh rather than firing duplicates.
  const refreshInFlight = useRef<Promise<WebSession | null> | null>(null);

  // Restore a persisted session on mount (page load / refresh). Without this,
  // the SDK may hold a valid user but our React state starts blank and the
  // route guard redirects to /login before anything restores it. The functional
  // setState only resolves the INITIAL "initializing" state — if a sign-in /
  // sign-out raced ahead (child effects fire before this parent effect), we
  // must not clobber it back.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let restored: WebSession | null = null;
      try {
        restored = auth.restore ? await auth.restore() : null;
      } catch (err) {
        console.warn("[auth] session restore failed:", err);
      }
      if (cancelled) return;
      setState((prev) =>
        prev.status === "initializing"
          ? restored !== null
            ? { status: "authenticated", session: restored }
            : { status: "anonymous" }
          : prev
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const signIn = useCallback(
    async (request: LoginRequest) => {
      setState({ status: "authenticating" });
      try {
        const { session } = await auth.signIn(request);
        setState({ status: "authenticated", session });
      } catch (err) {
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "sign-in failed",
        });
      }
    },
    [auth]
  );

  // [S-637 slice 2] Re-auth path for an expired session — see the type doc.
  // No `authenticating` transition, so App keeps rendering the authed tree
  // (state preserved) throughout. Rethrows so the modal shows an inline error.
  const reauthenticate = useCallback(
    async (request: LoginRequest) => {
      const { session } = await auth.signIn(request);
      setState({ status: "authenticated", session });
    },
    [auth]
  );

  const signOut = useCallback(async () => {
    try {
      await auth.signOut();
    } finally {
      setState({ status: "anonymous" });
      // S-203 — clear any caller-supplied side state (TanStack Query
      // cache in production). Wrapped so a failing handler doesn't
      // leave the user in a half-signed-out state.
      try {
        onSignOut?.();
      } catch (err) {
        console.error("[auth] onSignOut handler failed:", err);
      }
    }
  }, [auth, onSignOut]);

  const getFreshJwt = useCallback(async () => {
    if (state.status !== "authenticated") return null;
    const { session } = state;
    const dueForRefresh = Date.now() >= session.jwtExpiresAt - REFRESH_LEAD_MS;
    if (!dueForRefresh) return session.jwt;
    if (refreshInFlight.current === null) {
      refreshInFlight.current = auth.refresh().finally(() => {
        refreshInFlight.current = null;
      });
    }
    let refreshed: WebSession | null;
    try {
      refreshed = await refreshInFlight.current;
    } catch (err) {
      // The proactive refresh fires REFRESH_LEAD_MS *before* expiry, so a
      // transient failure (network blip, or Firebase's securetoken endpoint
      // momentarily unreachable — which surfaces as a raw "Failed to fetch"
      // TypeError) must not break in-flight requests while the current token
      // is still valid. Fall back to the existing JWT and let the next call
      // retry the refresh. Only surface the error once the token has actually
      // expired and we have nothing usable to send.
      if (Date.now() < session.jwtExpiresAt) {
        return session.jwt;
      }
      // [S-637 slice 2] Mode 2 — refresh threw past expiry. Keep the last
      // session (so the tree stays mounted for the re-auth modal) and normalize
      // the raw throw into a typed SessionExpiredError the UI recognizes.
      setState({ status: "expired", session });
      throw new SessionExpiredError({ cause: err });
    }
    if (refreshed === null) {
      // [S-637 slice 2] Mode 1 — refresh returned null past expiry (the refresh
      // token itself lapsed/was revoked). Recoverable via re-login, so surface
      // the re-auth modal (retain session) instead of a silent bounce to /login
      // that discards in-progress work. Throw the typed error so the doomed,
      // bearer-less request never fires.
      setState({ status: "expired", session });
      throw new SessionExpiredError();
    }
    setState({ status: "authenticated", session: refreshed });
    return refreshed.jwt;
  }, [auth, state]);

  // Auto-refresh at REFRESH_LEAD_MS before expiry so a new request never
  // needs to wait synchronously.
  useEffect(() => {
    if (state.status !== "authenticated") return;
    const ms = state.session.jwtExpiresAt - Date.now() - REFRESH_LEAD_MS;
    // getFreshJwt now throws SessionExpiredError when a refresh fails past
    // expiry (it has already flipped state to `expired`); swallow it here so
    // the background timer doesn't raise an unhandled rejection.
    if (ms <= 0) {
      void getFreshJwt().catch(() => {});
      return;
    }
    const timer = setTimeout(() => {
      void getFreshJwt().catch(() => {});
    }, ms);
    return () => clearTimeout(timer);
  }, [state, getFreshJwt]);

  // [S-202] Role-change refresh heartbeat — independent of the
  // expiry-driven refresh above. Polls auth.refresh() on a fixed
  // interval so a custom-claims change (role, accessTier, studioId)
  // surfaces in the UI within ROLE_REFRESH_INTERVAL_MS. If the refresh
  // returns null (claims went invalid — user revoked, tenant disabled)
  // the user is treated as signed out, mirroring getFreshJwt's
  // semantics.
  useEffect(() => {
    if (state.status !== "authenticated") return;
    const interval = roleRefreshIntervalMs ?? ROLE_REFRESH_INTERVAL_MS;
    if (interval <= 0) return;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      try {
        const refreshed = await auth.refresh();
        if (cancelled) return;
        if (refreshed === null) {
          // [S-637 slice 2] The background heartbeat caught the session lapsing
          // (refresh token revoked/expired). Same recoverable path as an
          // on-request expiry: retain the session and surface the re-auth modal
          // rather than silently dropping the user to anonymous mid-task.
          setState({ status: "expired", session: state.session });
          return;
        }
        // Replace the session only when something the UI surfaces
        // actually changed. Comparing the JWT directly would re-render
        // every tick (Firebase mints new strings even for unchanged
        // claims when forceRefresh is true).
        const prev = state.session;
        const changed =
          refreshed.role !== prev.role ||
          refreshed.accessTier !== prev.accessTier ||
          refreshed.studioId !== prev.studioId ||
          refreshed.tenantNamespace !== prev.tenantNamespace;
        if (changed) {
          setState({ status: "authenticated", session: refreshed });
        }
      } catch (err) {
        // Don't crash the app if the IDP is briefly unreachable —
        // next tick will retry. Surface to console for diagnosis.
        console.warn("[auth] role-refresh heartbeat failed:", err);
      }
    };
    const timer = setInterval(() => void tick(), interval);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [auth, state, roleRefreshIntervalMs]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, signIn, signOut, reauthenticate, getFreshJwt }),
    [state, signIn, signOut, reauthenticate, getFreshJwt]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error("useAuth must be called inside <AuthProvider>");
  }
  return value;
}

export function useSession(): WebSession | null {
  const { state } = useAuth();
  // S-411 — derive the EFFECTIVE session for the active tenant. The login token
  // carries the user's claims for ONE tenant; when they belong to several and
  // have switched, override role/studioId/tenantNamespace/accessTier with the
  // active tenant's membership so every consumer (RequireRole, all screens,
  // the API layer) reflects the right per-tenant role without re-login. The
  // context is null in tests / when not wrapped → no override (back-compat).
  const active = useContext(ActiveTenantContext);
  // [S-637 slice 2] `expired` still carries the last-known session. Return it so
  // screens under the re-auth modal keep rendering with a stable session (no
  // sudden-null crash) while the user signs back in. Reads are covered by the
  // modal overlay; every write 401s until re-auth succeeds anyway.
  if (state.status !== "authenticated" && state.status !== "expired") {
    return null;
  }
  return deriveEffectiveSession(state.session, active);
}
